import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 20px 0',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    fontSize: '13px',
  },
  headerCell: {
    padding: '8px 12px',
    fontWeight: 600,
    color: '#374151',
    textAlign: 'center',
    borderBottom: '2px solid #e5e7eb',
    whiteSpace: 'nowrap',
  },
  cornerCell: {
    padding: '8px 12px',
    fontWeight: 600,
    color: '#6b7280',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    textAlign: 'left',
    borderBottom: '2px solid #e5e7eb',
  },
  rowHeader: {
    padding: '8px 12px',
    fontWeight: 600,
    color: '#374151',
    textAlign: 'left',
    borderRight: '2px solid #e5e7eb',
    whiteSpace: 'nowrap',
  },
  cell: {
    padding: '8px 12px',
    textAlign: 'center',
    cursor: 'default',
    position: 'relative',
    minWidth: '48px',
    transition: 'opacity 0.15s ease',
  },
  netFlowCell: {
    padding: '8px 12px',
    textAlign: 'center',
    borderLeft: '2px solid #e5e7eb',
    whiteSpace: 'nowrap',
  },
  netFlowHeader: {
    padding: '8px 12px',
    fontWeight: 600,
    color: '#374151',
    textAlign: 'center',
    borderBottom: '2px solid #e5e7eb',
    borderLeft: '2px solid #e5e7eb',
    whiteSpace: 'nowrap',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
  },
  badgePositive: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  badgeNegative: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  badgeNeutral: {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
  },
  periodLabel: {
    marginTop: '16px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#9ca3af',
  },
  periodDates: {
    fontWeight: 600,
    color: '#6b7280',
  },
  tooltip: {
    position: 'fixed',
    backgroundColor: '#1f2937',
    color: '#ffffff',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    pointerEvents: 'none',
    zIndex: 9999,
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '14px',
  },
  toLabel: {
    padding: '0 0 4px 0',
    fontWeight: 600,
    color: '#6b7280',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    textAlign: 'center',
  },
};

export default function PriceBucketMigrationTable({ data }) {
  const [tooltip, setTooltip] = useState(null);

  const { colorScale, maxOffDiag } = useMemo(() => {
    if (!data || !data.migrations) return { colorScale: null, maxOffDiag: 0 };

    let max = 0;
    data.migrations.forEach((row, i) => {
      row.forEach((val, j) => {
        if (i !== j && val > max) max = val;
      });
    });

    const scale = d3.scaleSequential()
      .domain([0, Math.max(max, 1)])
      .interpolator(d3.interpolateOranges);

    return { colorScale: scale, maxOffDiag: max };
  }, [data]);

  const diagonalColorScale = useMemo(() => {
    if (!data || !data.migrations) return null;

    let maxDiag = 0;
    data.migrations.forEach((row, i) => {
      if (row[i] > maxDiag) maxDiag = row[i];
    });

    return d3.scaleSequential()
      .domain([0, Math.max(maxDiag, 1)])
      .interpolator(d3.interpolateBlues);
  }, [data]);

  const handleMouseEnter = (e, value, fromBucket, toBucket) => {
    if (value === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
      text: `${value} address${value !== 1 ? 'es' : ''} moved from ${fromBucket} to ${toBucket}`,
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  if (!data || !data.buckets || !data.migrations || data.migrations.length === 0) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Price Bucket Migration</h3>
        <p style={styles.subtitle}>Address movement between price ranges</p>
        <div style={styles.emptyState}>No migration data available</div>
      </div>
    );
  }

  const { buckets, fromDate, toDate, migrations, netFlows } = data;

  const netFlowMap = useMemo(() => {
    if (!netFlows) return {};
    const map = {};
    netFlows.forEach((nf) => {
      map[nf.bucket] = nf.netIn;
    });
    return map;
  }, [netFlows]);

  const getCellStyle = (rowIdx, colIdx, value) => {
    const base = { ...styles.cell };
    const isDiagonal = rowIdx === colIdx;

    if (value === 0) {
      base.backgroundColor = '#f9fafb';
      base.color = '#d1d5db';
      return base;
    }

    if (isDiagonal) {
      base.backgroundColor = diagonalColorScale(value);
      base.color = value > (diagonalColorScale.domain()[1] * 0.6) ? '#ffffff' : '#1e3a5f';
      base.fontWeight = 700;
    } else {
      base.backgroundColor = colorScale(value);
      base.color = value > (maxOffDiag * 0.6) ? '#ffffff' : '#7c2d12';
      base.fontWeight = 500;
    }

    return base;
  };

  const getNetFlowBadge = (bucket) => {
    const net = netFlowMap[bucket];
    if (net === undefined || net === null) return null;

    let badgeStyle;
    let label;

    if (net > 0) {
      badgeStyle = { ...styles.badge, ...styles.badgePositive };
      label = `+${net}`;
    } else if (net < 0) {
      badgeStyle = { ...styles.badge, ...styles.badgeNegative };
      label = `${net}`;
    } else {
      badgeStyle = { ...styles.badge, ...styles.badgeNeutral };
      label = '0';
    }

    return <span style={badgeStyle}>{label}</span>;
  };

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Price Bucket Migration</h3>
      <p style={styles.subtitle}>Address movement between price ranges</p>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.cornerCell}></th>
              <th
                colSpan={buckets.length}
                style={styles.toLabel}
              >
                To
              </th>
              <th style={{ ...styles.netFlowHeader, borderBottom: 'none' }}></th>
            </tr>
            <tr>
              <th style={styles.cornerCell}>From \ To</th>
              {buckets.map((bucket) => (
                <th key={`col-${bucket}`} style={styles.headerCell}>
                  {bucket}
                </th>
              ))}
              <th style={styles.netFlowHeader}>Net Flow</th>
            </tr>
          </thead>
          <tbody>
            {migrations.map((row, rowIdx) => (
              <tr key={`row-${rowIdx}`}>
                <td style={styles.rowHeader}>{buckets[rowIdx]}</td>
                {row.map((value, colIdx) => (
                  <td
                    key={`cell-${rowIdx}-${colIdx}`}
                    style={getCellStyle(rowIdx, colIdx, value)}
                    onMouseEnter={(e) =>
                      handleMouseEnter(e, value, buckets[rowIdx], buckets[colIdx])
                    }
                    onMouseLeave={handleMouseLeave}
                  >
                    {value > 0 ? value : ''}
                  </td>
                ))}
                <td style={styles.netFlowCell}>
                  {getNetFlowBadge(buckets[rowIdx])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {fromDate && toDate && (
        <div style={styles.periodLabel}>
          Period: <span style={styles.periodDates}>{fromDate}</span>
          {' \u2192 '}
          <span style={styles.periodDates}>{toDate}</span>
        </div>
      )}

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
